import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Tooltip, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import SettingsIcon from '@mui/icons-material/Settings';
import languageLearningApi from '../middleware/languageLearningApi';

const LanguageDashboardHeader = ({ 
  username = 'Learner', 
  courseTitle = '', 
  courseData = {},
  course = {}, 
  userId = '',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  const [greeting, setGreeting] = useState('');
  const [motivation, setMotivation] = useState('');
  const [userGoal, setUserGoal] = useState(null);
  
  // Language backgrounds with flags
  const languageBackgrounds = {
    en: {
      flag: "https://flagcdn.com/w320/us.png",
      name: "English"
    },
    de: {
      flag: "https://flagcdn.com/w320/de.png",
      name: "German"
    },
    es: {
      flag: "https://flagcdn.com/w320/es.png",
      name: "Spanish"
    },
    fr: {
      flag: "https://flagcdn.com/w320/fr.png",
      name: "French"
    },
    ko: {
      flag: "https://flagcdn.com/w320/kr.png",
      name: "Korean"
    },
    ja: {
      flag: "https://flagcdn.com/w320/jp.png",
      name: "Japanese"
    },
    zh: {
      flag: "https://flagcdn.com/w320/cn.png",
      name: "Chinese"
    },
    ar: {
      flag: "https://flagcdn.com/w320/ae.png",
      name: "Arabic"
    },
    ru: {
      flag: "https://flagcdn.com/w320/ru.png",
      name: "Russian"
    },
    pt: {
      flag: "https://flagcdn.com/w320/pt.png",
      name: "Portuguese"
    },
    default: {
      flag: "https://flagcdn.com/w320/un.png",
      name: "Language"
    }
  };
  
  // Goal options mapping
  const goalOptions = useMemo(() => [
    {
      id: 'beginner',
      title: 'Casual',
      icon: '🧘‍♂️',
      description: '2 new sentences + all vocab a day',
      sentenceCount: 2
    },
    {
      id: 'regular',
      title: 'Normal',
      icon: '📚',
      description: '4 new sentences + all vocab a day',
      sentenceCount: 4,
      recommended: true
    },
    {
      id: 'advanced',
      title: 'Serious',
      icon: '✍️',
      description: '6 new sentences + all associated vocab',
      sentenceCount: 6
    },
    {
      id: 'expert',
      title: 'Monk',
      icon: '🧘‍♀️',
      description: '8 new sentences + all associated vocab',
      sentenceCount: 8
    }
  ], []);
  
  // Navigate to course view
  const navigateToExplore = () => {
    if (course?._id) {
      window.location.href = `/view-course/${course._id}`;
    }
  };
  
  // Navigate to set daily goal
  const navigateToSetGoal = () => {
    if (course?._id) {
      window.location.href = `/set-daily-goal?${course._id}`;
    }
  };
  
  // Get random item from array
  const getRandomItem = (array) => {
    return array[Math.floor(Math.random() * array.length)];
  };
  
  // Fetch user goal data
  useEffect(() => {
    const fetchUserGoal = async () => {
      if (userId && course?._id) {
        try {
          const goalResponse = await languageLearningApi.get(`/user-goals/${userId}/${course._id}`);
          
          if (goalResponse.data && goalResponse.data.goal) {
            const sentenceCountForGoal = goalResponse.data.goal.sentenceCountForGoal;
            console.log('GoalRes ', goalResponse.data.goal);
            console.log('Sentence count ', sentenceCountForGoal);

            // Find matching goal option based on sentence count
            const matchedGoal = goalOptions.find(option => option.sentenceCount === sentenceCountForGoal);
            if (matchedGoal) {
              setUserGoal(matchedGoal);
            }
          }
        } catch (error) {
          console.error('Error fetching user goal:', error);
        }
      }
    };
    
    fetchUserGoal();
  }, [userId, course, goalOptions]);
  
  useEffect(() => {
    // Get current hour
    const currentHour = new Date().getHours();
    
    // Handle empty username
    const displayName = username || 'Learner';
    
    // Morning greetings (5:00 AM - 11:59 AM)
    const morningGreetings = [
      `Good morning, ${displayName}!`,
      `Rise and shine, ${displayName}!`,
      `Hello ${displayName}, ready for a productive day?`
    ];
    
    // Afternoon greetings (12:00 PM - 4:59 PM)
    const afternoonGreetings = [
      `Good afternoon, ${displayName}!`,
      `Hey there, ${displayName}!`,
      `Greetings, ${displayName}!`
    ];
    
    // Evening greetings (5:00 PM - 9:59 PM)
    const eveningGreetings = [
      `Good evening, ${displayName}!`,
      `Evening, ${displayName}!`,
      `Hello ${displayName}, finished your day strong?`
    ];
    
    // Night greetings (10:00 PM - 11:59 PM) - no username for odd hours
    const nightGreetings = [
      "Night owl mode activated!",
      "Burning the midnight oil?",
      "Late night language session?"
    ];
    
    // Late night/early morning greetings (12:00 AM - 4:59 AM) - no username for odd hours
    const lateNightGreetings = [
      "Couldn't sleep? Let's learn!",
      "Dedication level: Impressive!",
      "The quietest time for focused learning!"
    ];
    
    // Motivational statements
    const motivationalStatements = [
      "Every word you learn brings you closer to fluency.",
      "Consistency is the key to language mastery.",
      "Small steps today, big progress tomorrow.",
      "Challenge yourself with something new today.",
      "Your dedication is paying off - keep going!",
      "Learning happens one word at a time."
    ];
    
    // Select greeting based on time
    let timeBasedGreetings;
    
    if (currentHour >= 5 && currentHour < 12) {
      timeBasedGreetings = morningGreetings;
    } else if (currentHour >= 12 && currentHour < 17) {
      timeBasedGreetings = afternoonGreetings;
    } else if (currentHour >= 17 && currentHour < 22) {
      timeBasedGreetings = eveningGreetings;
    } else if (currentHour >= 22 && currentHour < 24) {
      timeBasedGreetings = nightGreetings;
    } else {
      timeBasedGreetings = lateNightGreetings;
    }
    
    // Set random greeting and motivation
    setGreeting(getRandomItem(timeBasedGreetings));
    setMotivation(getRandomItem(motivationalStatements));
  }, [username]); // Re-run when username changes
  
  // Custom fancy underline component
  const FancyUnderline = () => (
    <Box
      sx={{
        width: isMobile ? '60px' : '90px',
        height: '4px',
        margin: isMobile ? '8px auto' : '12px auto',
        background: 'linear-gradient(90deg, #2196f3 0%, #9c27b0 100%)',
        borderRadius: '2px',
      }}
    />
  );

  // Get language info
  const languageCode = course?.language || 'default';
  const languageInfo = languageBackgrounds[languageCode] || languageBackgrounds.default;
  
  // Language Learning component
  const LanguageLearningInfo = () => (
    <Tooltip title={`View ${languageInfo.name} course`}>
      <Box
        onClick={navigateToExplore}
        data-tour="view-course"
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          padding: '6px 12px',
          borderRadius: '8px',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(43, 108, 176, 0.08)',
          }
        }}
      >
        <Box 
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: isMobile ? 'center' : 'flex-start',
          }}
        >
          <Typography 
            variant="caption" 
            sx={{
              fontSize: isMobile ? '0.75rem' : '1rem',
              fontWeight: 650,
              color: 'black',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              mb: 0.5
            }}
          >
            Now Learning
          </Typography>
          
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <img 
              src={languageInfo.flag} 
              alt={`${languageInfo.name} flag`}
              style={{ 
                width: isMobile ? '20px' : '28px', 
                height: 'auto',
                marginRight: '8px',
                borderRadius: '4px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }} 
            />
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: '#2b6cb0',
                fontSize: isMobile ? '0.875rem' : '1rem',
              }}
            >
              {languageInfo.name}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Tooltip>
  );
  
  // Goal Commitment component
  const GoalCommitmentInfo = () => (
    userGoal && (
      <Box
        data-tour="goal-commitment"
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: '6px 12px',
          borderRadius: '8px',
        }}
      >
        <Box 
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: isMobile ? 'center' : 'flex-end',
          }}
        >
          <Typography 
            variant="caption" 
            sx={{
              fontSize: isMobile ? '0.75rem' : '1rem',
              fontWeight: 650,
              color: 'black',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              mb: 0.5
            }}
          >
            Current Commitment
          </Typography>
          
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: '#2b6cb0',
                fontSize: isMobile ? '0.875rem' : '1rem',
                marginRight: '8px'
              }}
            >
              {userGoal.icon} {userGoal.title}
            </Typography>
            <Tooltip title="Edit Goal">
              <IconButton 
                size="small" 
                onClick={navigateToSetGoal}
                sx={{
                  color: '#4A5568',
                  '&:hover': {
                    backgroundColor: 'rgba(43, 108, 176, 0.08)',
                    color: '#2b6cb0'
                  }
                }}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    )
  );
  
  // Greeting Component 
  const GreetingSection = () => (
    <motion.div
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.1 }}
      style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        width: '100%' 
      }}
    >
      <Typography 
        variant="h4" 
        component="h1" 
        sx={{
          fontWeight: 700,
          color: '#1a365d',
          fontSize: isMobile ? '1.5rem' : isTablet ? '1.75rem' : '2.25rem',
          letterSpacing: '-0.5px',
          textAlign: 'center'
        }}
      >
        {greeting}
      </Typography>
      <Typography 
        variant="subtitle1" 
        sx={{ 
          mt: 1,
          color: '#718096',
          fontWeight: 400,
          fontSize: isMobile ? '0.875rem' : '1rem',
          textAlign: 'center',
          px: isMobile ? 2 : 0
        }}
      >
        {motivation}
      </Typography>
      <FancyUnderline />
    </motion.div>
  );

  // Render layout based on screen size
  if (isMobile) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          mb: 1.5, // Reduced from 3
          mt: 4,    // Increased from 2
          px: 2,
          gap: 2
        }}
      >
        {/* Stacked layout for mobile */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <LanguageLearningInfo />
          {userGoal && <GoalCommitmentInfo />}
        </Box>
        
        <GreetingSection />
      </Box>
    );
  }
  
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        mb: 2,    // Reduced from 4
        mt: 4,    // Increased from 2
        position: 'relative',
        px: isTablet ? 2 : 3
      }}
    >
      {/* Responsive layout for tablet and desktop */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          width: '100%',
          mb: 2
        }}
      >
        <LanguageLearningInfo />
        <GreetingSection />
        {userGoal && <GoalCommitmentInfo />}
      </Box>
    </Box>
  );
};

export default LanguageDashboardHeader;